import { ExampleDriftContact } from 'constants/ExampleContact'
import * as R from 'ramda'
import React,
{ Component } from 'react'
import { connect } from 'react-redux'

import {
  Button,
  Flex,
  PageHeader,
  Tabs,
} from '@driftt/dds'
import { PropertyCreatorModal } from '@driftt/property-creator'
import { Input } from '@driftt/tide-core'
import { showTransientNotification } from '@driftt/traffic-control'
import {
  access,
  tracking 
} from '@driftt/traffic-control'
import {
  getAllContactProperties,
  getDriftProperties,
  getSalesforceSourcedProperties,
  getSystemProperties,
  updateContactPropertyStatus
} from 'api/contacts'
import { 
  getNewSidebarConfig,
  saveNewSidebarConfigExisting,
} from 'api/settings'
import { filterDriftProperties } from 'utils/filterDriftProperties'

import ArchiveConfirmationModal from './ArchiveConfirmationModal'
import PropertyLists from './PropertyLists'
import { ATTRIBUTE_STATUS } from './PropertyLists/constants'
import SettingsSidebarPreview from './SettingsSidebarPreview'

import './style.css'


class SettingsPage extends Component {
  constructor(props) {
    super(props)

    this.state = {
      activeTab: 'drift',
      defaultProperties: [],
      displayData: [],
      isDirty: false,
      isLoading: true,
      propertyNameFilter: "",
      savingProperties: false,
      showPropertyCreator: false,
      hasNoNewSettingsConfig: true,
      showArchiveConfirmationModal: false,
      attributeToUpdate: null,
      desiredAttributeStatus: null,
    }
  }

  componentDidMount() {
    tracking.trackEvent('Contact Settings - Page Viewed')
    window.addEventListener('beforeunload', this.onBeforeUnload)
  }

  componentWillUnmount() {
    window.removeEventListener('beforeunload', this.onBeforeUnload)
  }

  componentDidUpdate(prevProps) {
    if (prevProps.integrationStates.salesforce !== this.props.integrationStates.salesforce) {
      this.getProperties()
    }
  }

  onBeforeUnload = (event) => {
    if (this.state.isDirty) {
      event.preventDefault()
      event.returnValue = undefined
      return "Unsaved Changes"
    }
  }

  hasCorrectIntegration = () => access.hasGates('has_reveal')

  handleOnTabChange = activeTab => this.setState({ activeTab })

  sourcedPropertyListChange = (property, index) => {
    const { onUpdateSourcedProperty } = this.props
    const updateProperty = R.clone(property)

    if (R.isNil(property.isSelected)) {
      updateProperty.isSelected = true
    } else {
      updateProperty.isSelected = !property.isSelected
    }

    onUpdateSourcedProperty(updateProperty, index)
    this.setState({ isDirty: true }, this.prepareData)
  }

  propertyListChange = (property, index) => {
    const { onUpdateProperty } = this.props
    const prop = {
      ...property,
      isSelected: property.isSelected ? false : true
    }
    const trackedAction = property.isSelected ? 'Deselected' : 'Selected'
    tracking.trackEvent(`Contact Settings - ${trackedAction} Property`, {
      propertyName: property.name,
      propertyType: property.type,
      attributeType: property.attributeType
    })
    
    onUpdateProperty(prop, index)
    this.setState({ isDirty: true }, this.prepareData)
  }

  showPropertyListArchiveModal = (property, desiredStatus) => {
    if(desiredStatus === ATTRIBUTE_STATUS.ARCHIVED) {
      this.setState({
        showArchiveConfirmationModal: true,
        attributeToUpdate: property,
        desiredAttributeStatus: desiredStatus
      })
    } else {
      this.propertyListArchive(property, desiredStatus)
    }
  }

  propertyListArchive = (property, desiredStatus) => {
    const { onArchiveProperty } = this.props
    const prop = {
      ...property,
      status: desiredStatus
    }
    tracking.trackEvent(`Contact Settings - ${desiredStatus} Property`, {
      propertyName: property.name,
      propertyType: property.type,
      attributeType: property.attributeType
    })

    const getStatusDispayName = ({ status }) => status.toLowerCase() === "hidden" ? "archived" : status.toLowerCase()

    updateContactPropertyStatus({
      id: prop.id,
      status: prop.status 
    })
      .then(() => {
        onArchiveProperty(prop)
        this.props.showTransientNotification({
          type: "success",
          message: `Attribute ${getStatusDispayName(prop)}`
        })})
      .catch(() => this.props.showTransientNotification({
        type: "error",
        message: `Attribute failed to be ${getStatusDispayName(prop)}`
      }))
    this.setState({ showArchiveConfirmationModal: false })
  }

  setPropertyNameFilter = (event) => {
    this.setState({ propertyNameFilter: event.target.value })
  }

  cleanAttributesForNewConfig = attributes => {
    return attributes.map((attribute, index) => {
      const clonedAttr = R.clone(attribute)

      clonedAttr.enabled = true
      clonedAttr.displayIndex = index
      clonedAttr.source = (attribute.attributeType === 'drift' || attribute.attributeType === 'custom') ? "LEGACY" : "CLEARBIT_REVEAL"

      delete clonedAttr.graphType
      delete clonedAttr.id
      delete clonedAttr.index
      delete clonedAttr.status
      delete clonedAttr.isSelected
      delete clonedAttr.updatedAt
      delete clonedAttr.attributeType
      delete clonedAttr.type
      delete clonedAttr.orgId

      return clonedAttr
    })
  }

  getProperties = async () => {
    const {
      onReceiveDriftProperties,
      onReceiveCustomProperties,
      onReceiveArchivedProperties,
      onReceiveNewSidebarConfig,
      onReceiveSalesforceAttributes,
      integrationStates
    } = this.props

    try {
      const { data } = await getAllContactProperties()
      const { data: driftProperties } = await getDriftProperties()
      const { data: systemProperties } = await getSystemProperties()
      const { data: settings } = await getNewSidebarConfig()

      let salesforceProps = []

      if (integrationStates.salesforce){
        let salesforceAttributes = []
        try {
          salesforceAttributes = await getSalesforceSourcedProperties()
          salesforceAttributes = salesforceAttributes.data
        } catch (e) {
          console.error(e)
          salesforceAttributes = []
        }
        salesforceProps = salesforceAttributes
      }
      const arrayOfSelected = R.flatten(settings.map((card) => card.attributes.map(attribute => { return {
        name: attribute.name,
        source: attribute.source === "SALESFORCE" ? "SALESFORCE_CONTACT" : attribute.source 
      } })))
      const separatedProperties = filterDriftProperties(
        data, arrayOfSelected, driftProperties, systemProperties, salesforceProps
      )

      const selectedFirst = (a, b) => !a.isSelected - !b.isSelected

      onReceiveDriftProperties(separatedProperties.drift.sort(selectedFirst))
      onReceiveCustomProperties(separatedProperties.custom.sort(selectedFirst))
      onReceiveArchivedProperties(separatedProperties.archived.sort(selectedFirst))
      onReceiveSalesforceAttributes(separatedProperties.sfdc.sort(selectedFirst))
      onReceiveNewSidebarConfig(settings)

      this.setState(prevState => ({
        rawGroupedSettings: separatedProperties,
        savingProperties: false,
        rawSettings: settings,
        isLoading: !prevState.isLoading
      }), this.prepareData)
    } catch(e) {
      console.warn(e)
    }
  }
  determineNullState = () => {
    const { newConfigProperties } = this.props

    return (!R.isEmpty(newConfigProperties))
  }

  togglePropertyCreator = () => {
    this.setState(prevState => ({ showPropertyCreator: !prevState.showPropertyCreator }))
  }

  saveProperties = async () => {
    this.setState({
      savingProperties: true,
      isDirty: false
    })

    const {
      newConfigProperties,
      showTransientNotification 
    } = this.props

    tracking.trackEvent('Contact Settings - Saved Changes')

    try {
      await saveNewSidebarConfigExisting(newConfigProperties)

      this.getProperties()
      showTransientNotification({
        type: 'success',
        message: 'Sidebar settings saved successfully.' 
      })
    } catch(e) {
      console.warn(e)
    }
  }

  reorderSidebarCards = ({
    target,
    current 
  }) => {
    const {
      newConfigProperties,
      onReceiveNewSidebarConfig 
    } = this.props

    let clonedConfig = R.clone(newConfigProperties)

    clonedConfig = R.move(current, target, clonedConfig)
    clonedConfig = clonedConfig.map((card, idx) => ({
      ...card,
      displayIndex: idx 
    }))

    this.setState({ isDirty: true }, () => onReceiveNewSidebarConfig(clonedConfig))
  }

  reorderAttributes = ({
    target,
    current,
    settingsObject 
  }) => {
    const {
      newConfigProperties,
      onReceiveNewSidebarConfig 
    } = this.props

    const clonedConfig = R.clone(newConfigProperties)

    const editingIndex = R.findIndex(R.propEq('cardName', settingsObject), clonedConfig)

    clonedConfig[editingIndex].attributes = R.move(current, target, clonedConfig[editingIndex].attributes)

    clonedConfig[editingIndex].attributes.forEach((attribute, i) => {
      attribute.displayIndex = i
    })

    this.setState({ isDirty: true }, () => onReceiveNewSidebarConfig(clonedConfig))

  }

  render() {
    const {
      customProperties,
      archivedProperties,
      driftProperties,
      showTransientNotification,
      newConfigProperties,
      salesforceProperties,
      integrationStates 
    } = this.props
    const {
      activeTab,
      showPropertyCreator,
      isDirty,
      isLoading,
      savingProperties,
      propertyNameFilter,
      showArchiveConfirmationModal,
      attributeToUpdate,
      desiredAttributeStatus 
    } = this.state

    const tabData = [
      {
        label: 'Drift attributes',
        value: 'drift',
        content: 
          <PropertyLists
            nameFilter={propertyNameFilter}
            onChange={this.propertyListChange}
            properties={driftProperties}
            isLoading={isLoading}
            attributeType="Drift"
          />
        
      }, {
        label: 'Custom attributes',
        value: 'custom',
        content: 
          <PropertyLists
            nameFilter={propertyNameFilter}
            onChange={this.propertyListChange}
            onArchive={this.showPropertyListArchiveModal}
            properties={customProperties}
            isLoading={isLoading}
            attributeType="custom"
          />
      }
    ]
    if (integrationStates.salesforce){
      const salesforceTab = {
        label: 'Salesforce attributes',
        value: 'salesforce',
        content: 
          <PropertyLists
            nameFilter={propertyNameFilter}
            onChange={this.propertyListChange}
            properties={salesforceProperties}
            isSourced={true}
            isLoading={isLoading}
            attributeType="Salesforce"
          />
      }
      tabData.push(salesforceTab)
    }
    tabData.push({
      label: 'Archived',
      value: 'archived',
      content:
        <PropertyLists
          nameFilter={propertyNameFilter}
          onChange={this.propertyListChange}
          onArchive={this.showPropertyListArchiveModal}
          properties={archivedProperties}
          isLoading={isLoading}
          attributeType="archived"
        />
    })
    return (
      <>
        {showArchiveConfirmationModal && (
          <ArchiveConfirmationModal
            attributeToUpdate={attributeToUpdate}
            desiredAttributeStatus={desiredAttributeStatus}
            onArchive={this.propertyListArchive}
            onCancel={() =>
              this.setState({ showArchiveConfirmationModal: false })
            }
          />
        )}
        <Flex 
          direction="column" 
          fullHeight 
          fullWidth
          gap={0}
        >
          <PageHeader.Root>
            <PageHeader.Main>
              <PageHeader.Title>Contacts settings</PageHeader.Title>
              <PageHeader.RightElements>
                <Input
                  placeholder="Search attributes"
                  onChange={this.setPropertyNameFilter}
                  type="search"
                  className="contact-settings--filter-settings"
                />
                <Button onClick={this.togglePropertyCreator}>
                  Create new attribute
                </Button>
                <Button
                  variant="secondary"
                  onClick={this.saveProperties}
                  loading={savingProperties}
                  disabled={!isDirty}
                >
                  Save changes
                </Button>
              </PageHeader.RightElements>
            </PageHeader.Main>
          </PageHeader.Root>

          <Flex fullWidth>
            <Flex className="contact-settings-left-pane">
              <Tabs
                activeTab={activeTab}
                options={tabData}
                onValueChange={this.handleOnTabChange}
              />
            </Flex>

            <Flex className="contact-settings-right-pane" grow={0}>
              <SettingsSidebarPreview
                config={newConfigProperties}
                exampleContact={ExampleDriftContact}
                isEmpty={!this.determineNullState()}
                onReorderSidebarAttributes={this.reorderAttributes}
                onReorderSidebarCards={this.reorderSidebarCards}
              />
            </Flex>
          </Flex>
        </Flex>
        {showPropertyCreator ? (
          <PropertyCreatorModal
            isOpen={showPropertyCreator}
            onClose={this.togglePropertyCreator}
            onSaveSuccess={() => {
              this.getProperties()
              this.togglePropertyCreator()
              showTransientNotification({
                type: 'success',
                message: 'Contact attribute successfully created',
              })
            }}
            createType="CONTACT"
            hideOverlay={true}
          />
        ) : null}
      </>
    )
  }
}
const mapStateToProps = state => ({ integrationStates: state.integrations.status })

const mapDispatchToProps = dispatch => {
  return {
    showTransientNotification: config => {
      dispatch(showTransientNotification(config))
    }
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(SettingsPage)
